// src/hooks/useCustomerPricing.js

import { useState, useEffect, useCallback } from 'react';
import { collection, onSnapshot, getDocs, doc, setDoc, deleteDoc, query, where } from 'firebase/firestore';
import { db } from '../firebase';

const useCustomerPricing = (companyId) => {
  const [customerPrices, setCustomerPrices] = useState({});
  const [customerGroupPrices, setCustomerGroupPrices] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load customer-specific prices
  useEffect(() => {
    if (!companyId) return;

    const loadCustomerPrices = async () => {
      try {
        setLoading(true);
        const pricesRef = collection(db, 'companies', companyId, 'customerPrices');
        const snapshot = await getDocs(pricesRef);
        const prices = {};
        snapshot.forEach(doc => {
          const data = doc.data();
          if (!prices[data.customerId]) {
            prices[data.customerId] = {};
          }
          prices[data.customerId][data.productId] = data.price;
        });
        setCustomerPrices(prices);
      } catch (error) {
        console.error('Error loading customer prices:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCustomerPrices();
  }, [companyId]);

  // Get the last price for a specific customer and product
  const getCustomerProductPrice = useCallback((customerId, productId) => {
    if (customerPrices[customerId] && customerPrices[customerId][productId]) {
      return customerPrices[customerId][productId];
    }
    return null;
  }, [customerPrices]);

  // Get effective price considering: product-level > group+category > default
  const getEffectivePrice = useCallback((customerId, productId, product, customerGroupId) => {
    // 1. Check product-level customer price (highest priority)
    const productPrice = getCustomerProductPrice(customerId, productId);
    if (productPrice !== null) {
      return { price: productPrice, type: 'product', label: 'Özel Fiyat' };
    }

    // 2. Check group+category price
    if (customerGroupId && product?.groupId) {
      const groupPrice = customerGroupPrices.find(
        gp => gp.customerGroupId === customerGroupId && gp.productGroupId === product.groupId
      );
      if (groupPrice) {
        const defaultPrice = product.salePrice || 0;
        if (groupPrice.discountType === 'percentage') {
          const discountedPrice = defaultPrice * (1 - groupPrice.discountValue / 100);
          return { 
            price: Math.round(discountedPrice * 100) / 100, 
            type: 'group', 
            label: `Grup İndirimi (%${groupPrice.discountValue})`,
            discountValue: groupPrice.discountValue,
            discountType: 'percentage'
          };
        } else {
          // fixed price
          return { 
            price: groupPrice.discountValue, 
            type: 'group', 
            label: 'Grup Sabit Fiyat',
            discountType: 'fixed'
          };
        }
      }
    }

    // 3. Default price
    return { price: product?.salePrice || 0, type: 'default', label: null };
  }, [getCustomerProductPrice, customerGroupPrices]);

  // Save customer-specific price
  const saveCustomerPrice = useCallback(async (customerId, productId, price) => {
    if (!companyId) return;

    try {
      const priceRef = doc(collection(db, 'companies', companyId, 'customerPrices'));
      await setDoc(priceRef, {
        customerId,
        productId,
        price,
        createdAt: new Date()
      });

      // Update local state
      setCustomerPrices(prev => ({
        ...prev,
        [customerId]: {
          ...prev[customerId],
          [productId]: price
        }
      }));
    } catch (error) {
      console.error('Error saving customer price:', error);
      throw error;
    }
  }, [companyId]);

  // Save group+category price
  const saveGroupPrice = useCallback(async (customerGroupId, customerGroupName, productGroupId, productGroupName, discountType, discountValue) => {
    if (!companyId) return;

    try {
      // Check if a price already exists for this group+category
      const existingPrice = customerGroupPrices.find(
        gp => gp.customerGroupId === customerGroupId && gp.productGroupId === productGroupId
      );

      const priceData = {
        customerGroupId,
        customerGroupName,
        productGroupId,
        productGroupName,
        discountType,
        discountValue: Number(discountValue),
        updatedAt: new Date()
      };

      if (existingPrice) {
        // Update existing
        const priceRef = doc(db, 'companies', companyId, 'customerGroupPrices', existingPrice.id);
        await setDoc(priceRef, { ...priceData, createdAt: existingPrice.createdAt }, { merge: true });
      } else {
        // Create new
        const priceRef = doc(collection(db, 'companies', companyId, 'customerGroupPrices'));
        await setDoc(priceRef, { ...priceData, createdAt: new Date() });
      }
    } catch (error) {
      console.error('Error saving group price:', error);
      throw error;
    }
  }, [companyId, customerGroupPrices]);

  // Delete group+category price
  const deleteGroupPrice = useCallback(async (groupPriceId) => {
    if (!companyId) return;

    try {
      const priceRef = doc(db, 'companies', companyId, 'customerGroupPrices', groupPriceId);
      await deleteDoc(priceRef);
    } catch (error) {
      console.error('Error deleting group price:', error);
      throw error;
    }
  }, [companyId]);

  // Listen for real-time updates on customer prices
  useEffect(() => {
    if (!companyId) return;

    const pricesRef = collection(db, 'companies', companyId, 'customerPrices');
    const unsubscribe = onSnapshot(pricesRef, (snapshot) => {
      const prices = {};
      snapshot.docChanges().forEach(change => {
        if (change.type === 'added' || change.type === 'modified') {
          const data = change.doc.data();
          if (!prices[data.customerId]) {
            prices[data.customerId] = {};
          }
          prices[data.customerId][data.productId] = data.price;
        } else if (change.type === 'removed') {
          const data = change.doc.data();
          if (prices[data.customerId] && prices[data.customerId][data.productId]) {
            delete prices[data.customerId][data.productId];
          }
        }
      });

      setCustomerPrices(prev => ({
        ...prev,
        ...prices
      }));
    });

    return () => unsubscribe();
  }, [companyId]);

  // Listen for real-time updates on customer group prices
  useEffect(() => {
    if (!companyId) return;

    const groupPricesRef = collection(db, 'companies', companyId, 'customerGroupPrices');
    const unsubscribe = onSnapshot(groupPricesRef, (snapshot) => {
      const prices = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCustomerGroupPrices(prices);
    });

    return () => unsubscribe();
  }, [companyId]);

  // Listen for real-time updates on customer groups
  useEffect(() => {
    if (!companyId) return;

    const groupsRef = collection(db, 'companies', companyId, 'customerGroups');
    const unsubscribe = onSnapshot(groupsRef, (snapshot) => {
      const groups = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCustomerGroups(groups);
    });

    return () => unsubscribe();
  }, [companyId]);

  // Save a new customer group
  const saveCustomerGroup = useCallback(async (groupName) => {
    if (!companyId || !groupName) return;

    try {
      const groupRef = doc(collection(db, 'companies', companyId, 'customerGroups'));
      await setDoc(groupRef, {
        name: groupName.trim(),
        createdAt: new Date()
      });
      return groupRef.id;
    } catch (error) {
      console.error('Error saving customer group:', error);
      throw error;
    }
  }, [companyId]);

  return {
    customerPrices,
    customerGroupPrices,
    customerGroups,
    loading,
    getCustomerProductPrice,
    getEffectivePrice,
    saveCustomerPrice,
    saveGroupPrice,
    deleteGroupPrice,
    saveCustomerGroup
  };
};

export default useCustomerPricing;